import { useState } from "react";
import { PageHeader, Card, ModeBadge } from "@/components/kit";
import {
  Rocket, Building2, Fingerprint, Sparkles, CalendarDays, CheckCircle2, Pencil,
  Images, Youtube, Plug, BarChart3, Lightbulb, Shield, ChevronDown, Info,
} from "lucide-react";

const SECTIONS = [
  {
    id: "inicio", icon: Rocket, title: "1. Primeiros passos (login e organização)",
    mode: "REAL",
    logic: "Ao entrar, o sistema valida seu e-mail/senha (JWT) e carrega as organizações às quais você pertence. Tudo é multiempresa: cada dado fica isolado por organização.",
    steps: [
      "Faça login com sua conta. Novos usuários que se registram viram Owner de uma nova organização.",
      "No topo, use o seletor de empresa para escolher a organização ativa (todo o painel passa a mostrar os dados dela).",
      "Se você é Super Admin, também vê o item 'Painel Super Admin' no menu.",
    ],
  },
  {
    id: "empresas", icon: Building2, title: "2. Empresas / Multiempresa e Equipe (RBAC)",
    mode: "REAL",
    logic: "Uma organização pode ter várias marcas e vários membros. Cada membro tem um papel (RBAC) que define o que pode fazer — validado no servidor a cada ação.",
    steps: [
      "Crie sua marca real: nome, nicho, site e cor. (Papéis Owner/Admin/Marketing podem criar.)",
      "Adicione membros da equipe com e-mail, senha inicial e papel: Owner, Admin, Marketing, Editor, Approver ou Viewer.",
      "Lógica dos papéis: Editor cria/edita conteúdo; Approver aprova; Viewer só visualiza; Owner/Admin gerenciam tudo.",
      "Precisa de outra organização? Crie em 'Nova organização' — os dados ficam totalmente separados.",
    ],
  },
  {
    id: "dna", icon: Fingerprint, title: "3. DNA da Marca (base da IA)",
    mode: "REAL",
    logic: "O DNA é a identidade que alimenta a IA. Quanto mais completo, melhores e mais fiéis ficam os conteúdos gerados.",
    steps: [
      "Selecione a marca e preencha: missão, público-alvo, palavras-chave, concorrentes e tons de voz permitidos.",
      "Envie o logo — ele é salvo no armazenamento em nuvem (Object Storage real) e usado como identidade visual.",
      "Clique em 'Salvar DNA'. Esses dados são enviados à IA em toda geração de campanha/roteiro/arte.",
    ],
  },
  {
    id: "campanhas", icon: Sparkles, title: "4. Gerador de Campanhas com IA",
    mode: "REAL",
    logic: "A IA (GPT-5.4) recebe o DNA da marca + o tema e monta um planejamento editorial dia a dia. O controle anti-repetição evita repetir temas usados recentemente.",
    steps: [
      "Escolha a marca, o período (7, 15 ou 30 dias) e os canais (Instagram, Facebook, Google, WhatsApp, YouTube, YouTube Shorts, TikTok).",
      "Defina o tema principal e a janela de anti-repetição (30/60/90/180/365 dias).",
      "Clique em 'Gerar planejamento'. Cada dia recebe título, tema, canal e legenda com CTA.",
      "Os conteúdos entram automaticamente como 'Pendente de Aprovação'. Vá ao Calendário ou à Aprovação.",
    ],
  },
  {
    id: "calendario", icon: CalendarDays, title: "5. Calendário Editorial",
    mode: "REAL",
    logic: "Visão do planejamento distribuído por dia. Cada card mostra canal, tema e o status atual do conteúdo.",
    steps: [
      "Filtre por marca (ou veja todas).",
      "Acompanhe o status de cada dia: Pendente, Aprovado, Ajuste Solicitado, Programado ou Publicado (Sandbox).",
    ],
  },
  {
    id: "aprovacao", icon: CheckCircle2, title: "6. Central de Aprovação (fluxo de status)",
    mode: "SANDBOX",
    logic: "Aqui o conteúdo caminha por um fluxo real de estados. Aprovar/Rejeitar exige papel de Approver. Agendar cria um job no scheduler. O scheduler, quando rodado, marca como 'Publicado (Sandbox)' — nunca envia à rede real sem conexão oficial.",
    steps: [
      "Filtre por status. Em cada linha use: Editar, Aprovar, Solicitar ajuste ou Agendar.",
      "Ao 'Agendar', é criado um job com data/hora. O sistema publica automaticamente no horário agendado.",
      "IMPORTANTE: a publicação fica marcada como SANDBOX (publish_confirmed = false). Só vira publicação real quando o canal estiver conectado por OAuth oficial.",
    ],
  },
  {
    id: "editor", icon: Pencil, title: "7. Editor de Post + Imagem com IA",
    mode: "REAL",
    logic: "No editor você ajusta o conteúdo por canal. A arte é gerada de verdade pela IA (Gemini Nano Banana) a partir do DNA da marca e salva no Object Storage.",
    steps: [
      "Na Aprovação, clique no ícone de lápis (Editar) de um post.",
      "Ajuste título, legenda e hashtags. Veja as hashtags formatadas em tempo real.",
      "Clique em 'Gerar com IA' para criar a arte automaticamente, ou 'Enviar' para subir sua própria imagem.",
      "Clique em 'Salvar' — as alterações e a imagem ficam vinculadas ao post.",
    ],
  },
  {
    id: "midia", icon: Images, title: "8. Biblioteca de Mídia",
    mode: "REAL",
    logic: "Repositório central de logos, fotos e vídeos, com armazenamento em nuvem real (Object Storage). Imagens geradas por IA também aparecem aqui.",
    steps: [
      "Escolha a categoria e envie o arquivo (imagem, vídeo ou PDF).",
      "Filtre por categoria e visualize as miniaturas.",
      "Remova o que não usar (exclusão lógica).",
    ],
  },
  {
    id: "youtube", icon: Youtube, title: "9. YouTube Studio",
    mode: "REAL",
    logic: "A IA gera o pacote completo do vídeo (título, descrição, roteiro, capítulos, tags, ideia de thumbnail). A thumbnail pode ser gerada como imagem real.",
    steps: [
      "Selecione a marca, o tema, o formato (vídeo ou Short) e a duração.",
      "Clique em 'Gerar roteiro' e acompanhe o pipeline sendo preenchido.",
      "Use 'Gerar thumbnail com IA' para criar a arte da capa.",
      "Em Content Repurposing, defina para quais formatos/canais o vídeo será reaproveitado (Shorts, TikTok, Reels, Stories...).",
    ],
  },
  {
    id: "conexoes", icon: Plug, title: "10. Central de Conexões (OAuth real x Sandbox)",
    mode: "SANDBOX",
    logic: "Lógica de honestidade: um canal só é 'conectado' quando a API oficial confirma. Se as credenciais de app da rede não estiverem configuradas, o botão 'Conectar OAuth' avisa exatamente o que falta e o canal permanece em SANDBOX. Nada é publicado de verdade sem essa confirmação.",
    steps: [
      "Veja cada canal com o indicador 'Credenciais: configuradas/pendentes'.",
      "Clique em 'Conectar OAuth': se as credenciais existirem, você é levado à autorização oficial; se não, recebe um aviso do que configurar.",
      "Para Instagram/Facebook/WhatsApp é preciso um app Meta (META_APP_ID/SECRET). Para YouTube/Google, credenciais Google. Para TikTok, TIKTOK_CLIENT_KEY/SECRET.",
      "Enquanto pendente, o canal fica em SANDBOX — a estrutura já está pronta para ativar assim que as credenciais forem informadas.",
    ],
  },
  {
    id: "analytics", icon: BarChart3, title: "11. Análises (métricas honestas)",
    mode: "SANDBOX",
    logic: "O sistema nunca inventa números. Mostra apenas contadores internos reais (por status e por canal). Métricas externas (alcance, impressões, engajamento) aparecem só depois de conectar o canal oficialmente.",
    steps: [
      "Acompanhe totais e gráficos por status e por canal (dados reais do seu conteúdo).",
      "O card 'Métricas externas' fica 'Aguardando conexão' até um canal ser vinculado por OAuth oficial.",
    ],
  },
  {
    id: "ideias", icon: Lightbulb, title: "12. Caixa de Ideias",
    mode: "REAL",
    logic: "Um lugar para registrar pautas e insights que depois viram temas de campanha.",
    steps: [
      "Digite a ideia e salve (Enter ou botão).",
      "Reaproveite as ideias como tema no Gerador de Campanhas.",
    ],
  },
  {
    id: "admin", icon: Shield, title: "13. Painel Super Admin",
    mode: "REAL",
    logic: "Visão global do sistema (somente Super Admin): organizações, usuários, consumo e custo de IA, estado de cada provider (REAL/SANDBOX/MOCK), planos configuráveis e auditoria de ações.",
    steps: [
      "Acompanhe o consumo de tokens e o custo estimado de IA.",
      "Veja todas as organizações e o estado real de cada integração.",
      "Consulte os planos e o log de auditoria (quem fez o quê e quando).",
    ],
  },
];

const LEGEND = [
  { mode: "REAL", text: "Funcionalidade real e ativa agora." },
  { mode: "SANDBOX", text: "Estrutura pronta em ambiente de teste; sem confirmação de API oficial." },
  { mode: "MOCK", text: "Simulação; recurso planejado para versão futura (ex.: geração de vídeo)." },
];

export default function Ajuda() {
  const [open, setOpen] = useState("inicio");

  return (
    <div>
      <PageHeader title="Guia de Uso" subtitle="Passo a passo de todo o app, com a lógica real de cada situação." />

      <Card className="mb-6 bg-slate-50/60">
        <div className="flex items-start gap-3">
          <Info size={18} className="text-[#3457d5] mt-0.5 shrink-0" />
          <div>
            <p className="text-sm text-slate-700 font-medium mb-2">Legenda de estados (transparência total):</p>
            <div className="grid sm:grid-cols-3 gap-3">
              {LEGEND.map((l) => (
                <div key={l.mode} className="flex items-start gap-2">
                  <ModeBadge mode={l.mode} />
                  <span className="text-xs text-slate-500">{l.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Card>

      <div className="space-y-3">
        {SECTIONS.map((s) => {
          const isOpen = open === s.id;
          return (
            <Card key={s.id} className="p-0 overflow-hidden" data-testid={`help-${s.id}`}>
              <button onClick={() => setOpen(isOpen ? "" : s.id)}
                className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-slate-50 transition-colors" data-testid={`help-toggle-${s.id}`}>
                <div className="h-10 w-10 rounded-xl pf-gradient flex items-center justify-center text-white shrink-0"><s.icon size={18} /></div>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold text-slate-900">{s.title}</div>
                </div>
                <ModeBadge mode={s.mode} />
                <ChevronDown size={18} className={`text-slate-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
              </button>
              {isOpen && (
                <div className="px-5 pb-5 pt-1 border-t border-slate-100">
                  <div className="rounded-xl bg-blue-50/60 border border-blue-100 p-3 text-sm text-slate-700 mb-4">
                    <b className="text-[#3457d5]">Lógica:</b> {s.logic}
                  </div>
                  <ol className="space-y-2.5">
                    {s.steps.map((step, i) => (
                      <li key={i} className="flex items-start gap-3 text-sm text-slate-600">
                        <span className="h-6 w-6 rounded-full bg-slate-100 text-slate-600 text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                        <span>{step}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}
